/**
 * <trade-in-uae-only> — trade-in is for shoppers in the UAE only (merchant decision 2026-11-03):
 * we collect, inspect and pay in the UAE. Used by the trade-in page (sections/trade-in.liquid)
 * and the PDP banner (snippets/trade-in-pdp-banner.liquid).
 *
 * Children: [data-ti-uae] (shown in the UAE) and optionally [data-ti-abroad] (shown outside it).
 *
 * Location comes from the theme's single geo source: window.tbGeo.resolveOutsideUae()
 * (assets/express-delivery.js, in <head>; also used by UAE-only announcement slides), never a
 * fresh IP check — so trade-in can't disagree with the GCC prices and delivery promise.
 * Unknown → treated as UAE (most visitors are; the terms say UAE only and staff decline in
 * WhatsApp). The opposite would hide trade-in from UAE shoppers whose IP lookup fails.
 *
 * Hidden with `hidden`, never removed, and re-checked on `on:emirate:change` and on a BFCache
 * restore (pageshow.persisted), per the geo-gated UI rules.
 */
class TradeInUaeOnly extends HTMLElement {
  constructor() {
    super();
    this.refresh = this.refresh.bind(this);
    this.onPageShow = (event) => {
      if (event.persisted) this.refresh();
    };
  }

  connectedCallback() {
    this.eventName = (window.tbGeo && window.tbGeo.EMIRATE_CHANGE_EVENT) || 'on:emirate:change';
    document.addEventListener(this.eventName, this.refresh);
    window.addEventListener('pageshow', this.onPageShow);
    // express-delivery.js sits in <head>, so tbGeo normally exists already; wait if it doesn't.
    if (window.tbGeo || document.readyState !== 'loading') this.refresh();
    else document.addEventListener('DOMContentLoaded', this.refresh, { once: true });
  }

  disconnectedCallback() {
    document.removeEventListener(this.eventName, this.refresh);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  static async outsideUae() {
    const geo = window.tbGeo;
    if (!geo || !geo.resolveOutsideUae) return false;
    try {
      return await geo.resolveOutsideUae();
    } catch (e) {
      return false;
    }
  }

  async refresh() {
    const abroad = await TradeInUaeOnly.outsideUae();
    this.classList.toggle('is-abroad', abroad);
    this.querySelectorAll('[data-ti-uae]').forEach((el) => {
      el.hidden = abroad;
    });
    this.querySelectorAll('[data-ti-abroad]').forEach((el) => {
      el.hidden = !abroad;
    });
  }
}

if (!customElements.get('trade-in-uae-only')) customElements.define('trade-in-uae-only', TradeInUaeOnly);
